/* eslint-disable @typescript-eslint/naming-convention -- enum members are PascalCase, as TypeScript enums conventionally are */
/* eslint-disable no-shadow -- the core rule reports every TypeScript enum as shadowing itself */
/**
 * Typed contract for the Dynamics custom API `indskr_GetPricingBundle`.
 *
 * See docs/features/order-capture.md for the full analysis.
 */

/** `indskr_discountlist.type` */
export enum DiscountListType {
  Discount = 268890000,
  FreeGoods = 268890001,
}

/** `indskr_discountlist.valueType` */
export enum DiscountValueType {
  Percentage = 268890000,
  FlatAmount = 268890001,
}

/**
 * `indskr_discountlist.indskr_offershape`: which traversal a list's rules follow. It replaced
 * `indskr_basis` (removed 30 Sep 2026); one band column means a different quantity per shape.
 */
export enum OfferShape {
  VolumeSingleProduct = 268890000,
  CrossProductOnQualifier = 268890001,
  BasketSum = 268890002,
  OrderLevel = 268890003,
  FreeGoodsSameProduct = 268890004,
  FreeGoodsCrossProduct = 268890005,
  CrossProductOnAnother = 268890006,
}

/** Rules and free goods pair up through `offerGroup` using these roles. */
export enum OfferRole {
  Qualifying = 268890000,
  Reward = 268890001,
}

/**
 * Whether a price list's value minimums are tested BEFORE or AFTER discounts.
 * One basis governs `minOrderValue` and `minDeliveryValue` together, deliberately.
 */
export enum MinimumValueBasis {
  Gross = 268890000,
  Net = 268890001,
}

/**
 * Qualifies a discount rule's threshold cap — the same two meanings for the amount cap and
 * the percentage cap. `Cap` is a ceiling on the discount given; `Base` is a ceiling on the
 * value the discount may be applied to.
 */
export enum ThresholdUsage {
  Cap = 268890000,
  Base = 268890001,
}

/**
 * Evaluation level of a free-goods row or a discount rule (doc 02 PB-022): judged on the whole
 * order, or on each delivery alone. The name predates rules carrying it.
 */
export enum FreeGoodEvaluationLevel {
  CompleteOrder = 268890000,
  SplitOrder = 268890001,
}

export interface PricingItem {
  id: string;
  /** Product GUID. Rx/OTC still has no source anywhere. */
  product: string;
  /** Since io-crm 906e05ccc — removes the BrandOfflineService lookup for name and code. */
  productName: string | null;
  productCode: string | null;
  unitPrice: number;
  /** The unit's id, sent back as a line's `uomId`; a bundle older than plug-in 1.0.0.4 holds its name. */
  uom: string | null;
  /** The unit's name, for display. */
  uomName?: string | null;
  validFrom: string | null;
  validTo: string | null;
  minQuantity: number | null;
  maxQuantity: number | null;

  /** Product GUID of the category band the row sits in — drives matrix row grouping. */
  productCategory: string | null;
  /** Added on the device at sync: the bundle names the category by id only. */
  productCategoryName?: string | null;
  /** Stepper increment for this SKU on this price list. Commonly 5/10/20/50, rarely 1. */
  incrementQuantity: number | null;
  /** Per-account allowance, scoped by the price list's rollup query. Not enforced server-side. */
  quotaPerAccount: number | null;
  /** Star facet in the product catalogue. */
  isRecommended: boolean | null;

  version: number;
}

export interface PricingDiscountRule {
  id: string;
  /** Null on a basket's applied rule, or a volume rule offered to every product. */
  product: string | null;
  productName: string | null;
  productCode: string | null;
  role: OfferRole;
  /** Non-null pairs this rule with its counterpart (buy X get Y discounted). */
  offerGroup: number | null;
  percentage: number | null;
  amount: number | null;
  /** Volume band. */
  minQuantity: number | null;
  maxQuantity: number | null;
  /** Caps the units discounted: beyond it the discount is computed as if this many were bought. */
  thresholdCount: number | null;
  /** A currency limit on the rule, qualified by `thresholdUsage`; not an eligibility minimum. */
  thresholdAmount: number | null;
  /**
   * Qualifies `thresholdAmount` (`indskr_thresholdamountusage`): `Cap` (Maximum Discount) the most
   * the rule takes off, `Base` (Maximum Value) the most value it applies to. **Null is a real
   * state** — nobody configured a meaning, and the device must not pick one.
   */
  thresholdUsage: ThresholdUsage | null;
  /**
   * The percentage counterpart to `thresholdAmount` — same row, different unit. With
   * `thresholdPercentageUsage === ThresholdUsage.Cap` this is **the max-discount ceiling**
   * the engine has been defaulting to 100% for want of a source (`PricingInput.maxDiscountPct`).
   * Null means no percentage cap, which is distinct from a configured zero.
   */
  thresholdPercentage: number | null;
  /** Qualifies `thresholdPercentage`. Null is unconfigured, not a default. */
  thresholdPercentageUsage: ThresholdUsage | null;
  /** Complete order vs split order. Null means unconfigured — do not infer. */
  evaluationLevel: FreeGoodEvaluationLevel | null;
  /** Cross-product child not mapped to the caller's catalogue. */
  unmapped: boolean;
  version: number;
}

export interface PricingFreeGood {
  id: string;
  product: string;
  productName: string | null;
  productCode: string | null;
  role: OfferRole;
  offerGroup: number | null;
  quantity: number;
  isRepeatable: boolean;
  maxRewardQuantity: number | null;
  evaluationLevel: FreeGoodEvaluationLevel | null;
  unmapped: boolean;
  version: number;
}

export interface PricingDiscountList {
  id: string;
  type: DiscountListType;
  valueType: DiscountValueType;
  /** Null on a list saved before the column; the engine then infers it from the rules. */
  offerShape: OfferShape | null;
  /** Lower wins. Candidate tie-break for non-stackable matches — see docs, ask S1. */
  priority: number | null;
  isStackable: boolean;
  validFrom: string | null;
  validTo: string | null;
  version: number;
  discountRules: PricingDiscountRule[];
  freeGoods: PricingFreeGood[];
}

export interface PricingPriceList {
  id: string;
  name: string;
  /** Display name ("US Dollar"): localised and editable, so it identifies nothing. */
  currency: string;
  /** `transactioncurrencyid` — what an order on this list is denominated in, create-only. */
  currencyId?: string | null;
  /** ISO 4217 code ("USD", "CAD"). */
  currencyCode?: string | null;
  /** Decimal places amounts are allocated to. Null when the server could not read it. */
  currencyPrecision?: number | null;
  /** Gates the whole order's value. */
  minOrderValue: number | null;
  /** Gates the whole order's quantity. */
  minOrderQuantity: number | null;
  /** Gates a single delivery drop's value — stops a split producing unshippable drops. */
  minDeliveryValue: number | null;
  /** Gates a single delivery drop's quantity. */
  minDeliveryQuantity: number | null;
  /**
   * Whether `minOrderValue` and `minDeliveryValue` are tested against gross (before
   * discounts) or net (after). **Null means unconfigured** — a real third state, distinct
   * from either option, so the device must not infer a commercial rule nobody chose.
   */
  minimumValueBasis: MinimumValueBasis | null;
  /**
   * Account rollup query as FetchXML — what lets one user-scoped bundle be resolved locally
   * to the right list for the selected account. Conditions compare RAW attribute values
   * (option-set numbers), never formatted labels.
   */
  fetchXml: string | null;
  /** Lower wins. */
  priority: number;
  isDefault: boolean;
  validFrom: string | null;
  validTo: string | null;
  /**
   * Bumped on publish, together with every child. Use it to invalidate a derived index —
   * NOT as a write gate, and NOT to compare content across projections: the same list at
   * the same version is filtered differently per caller.
   */
  version: number;
  items: PricingItem[];
  discountLists: PricingDiscountList[];
}

/** Derived by the server from whether `knownPriceLists` was sent. Never an input. */
export type PricingBundleMode = 'new' | 'delta';

/** A price list the device declared but is no longer entitled to. */
export interface PricingRemovedPriceList {
  id: string;
  /** e.g. notEligible / expired / unpublished / deleted */
  reason: string;
}

/**
 * Response envelope.
 *
 * `priceLists` was REPLACED by `changed` / `unchanged` / `removed` with no compatibility
 * alias — a parser looking for the old key finds nothing.
 */
export interface PricingBundle {
  status: string;
  complete: boolean;
  scope: PricingBundleScope;
  bundleVersion: string;
  mode: PricingBundleMode;

  /**
   * Every eligible price list, in full. Despite the name this is NOT a diff — it carries
   * exactly what `priceLists` used to, so the device still replaces wholesale.
   */
  changed: PricingPriceList[];

  /**
   * Always empty at present. The key exists so the client can code against it once, and
   * will populate when per-price-list versioning lands.
   */
  unchanged: string[];

  /** Tombstones for ids sent in `knownPriceLists` that are no longer eligible. */
  removed: PricingRemovedPriceList[];
}

export type PricingBundleScope = 'all' | 'single';

/** One PouchDB document per request projection. */
export interface PricingBundleDoc {
  _id: string;
  bundleVersion: string;
  scope: PricingBundleScope;
  positionIds: string[];
  accountId: string | null;
  fetchedAt: string;
  bundle: PricingBundle;
}
